// src/features/mech/plastics/questions/PartWeightDimensionsQuestion.jsx
import React from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import PerlTextField from "../../../../shared/components/PerlTextField";

export default function PartWeightDimensionsQuestion({ value, onChange }) {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  // Parse the value object or initialize with empty strings
  const dimensionValue = value || {
    measurementBasis: "repeat",
    pouchLength: "",
    pouchWidth: "",
    sealAllowance: "",
    gusset: "",
    partLength: "",
    partWidth: "",
    wastagePct: "",
  };

  const handleFieldChange = (field, newValue) => {
    onChange?.({
      ...dimensionValue,
      [field]: newValue,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      <Box>
        <Typography
          sx={{
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            mb: 1,
          }}
        >
          Basis
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={dimensionValue.measurementBasis || "repeat"}
          onChange={(_, newValue) => {
            if (newValue) {
              handleFieldChange("measurementBasis", newValue);
            }
          }}
          sx={{
            "& .MuiToggleButton-root": {
              color: perlColors.textSecondary,
              borderColor: alpha(perlColors.textPrimary, 0.12),
              textTransform: "none",
              fontWeight: 500,
              "&.Mui-selected": {
                color: perlColors.textPrimary,
                bgcolor: alpha(brandColor, 0.16),
                borderColor: alpha(brandColor, 0.8),
              },
            },
          }}
        >
          <ToggleButton value="pouch">Pouch/Sachet</ToggleButton>
          <ToggleButton value="repeat">Repeat × Coil width</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {dimensionValue.measurementBasis === "pouch" ? (
        <>
          <Typography sx={{ color: perlColors.textSecondary, fontSize: "0.82rem" }}>
            Section 3A: Pouch/Sachet
          </Typography>
          <PerlTextField
            label="Pouch L (mm)"
            required
            type="number"
            value={dimensionValue.pouchLength || ""}
            onChange={(e) => handleFieldChange("pouchLength", e.target.value)}
          />
          <PerlTextField
            label="Pouch W (mm)"
            required
            type="number"
            value={dimensionValue.pouchWidth || ""}
            onChange={(e) => handleFieldChange("pouchWidth", e.target.value)}
          />
          <PerlTextField
            label="Seal allowance (mm)"
            type="number"
            value={dimensionValue.sealAllowance || ""}
            onChange={(e) => handleFieldChange("sealAllowance", e.target.value)}
          />
          <PerlTextField
            label="Gusset (mm)"
            type="number"
            value={dimensionValue.gusset || ""}
            onChange={(e) => handleFieldChange("gusset", e.target.value)}
          />
        </>
      ) : (
        <>
          <Typography sx={{ color: perlColors.textSecondary, fontSize: "0.82rem" }}>
            Section 3B: Repeat basis
          </Typography>
      <PerlTextField
        label="Repeat (mm)"
        required
        type="number"
        value={dimensionValue.partLength || ""}
        onChange={(e) => handleFieldChange("partLength", e.target.value)}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Coil width (mm)"
        required
        type="number"
        value={dimensionValue.partWidth || ""}
        onChange={(e) => handleFieldChange("partWidth", e.target.value)}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
          <PerlTextField
            label="Wastage (%)"
            type="number"
            value={dimensionValue.wastagePct || ""}
            onChange={(e) => handleFieldChange("wastagePct", e.target.value)}
          />
        </>
      )}
    </Box>
  );
}

